import React, { useState } from 'react'
import { Alert, Popover, Tag, Table } from 'antd'
import { padStart } from 'lodash'
import moment from 'moment'
import ParkedSaleStatusButton from './ParkedSaleStatusButton'
import { ParkedSaleStore, ParkedSaleTypes } from './ParkedSaleStore'
import { IPaymentQueueWithExternalSource, PaymentQueueStatus } from '@getgreenline/homi-shared'
import { ParkedSaleButtonStatus } from './ParkedSaleStatusButtonLists'
import { BLAZE_COMPANY } from '../../../../constants/general'

interface Props {
  alertPaymentQueue: IPaymentQueueWithExternalSource[]
  parkedSaleType: string
  parkedSaleStore: ParkedSaleStore
}

export const ParkedSaleAlert = ({ alertPaymentQueue, parkedSaleType, parkedSaleStore }: Props) => {
  const { compareOneHourFromNow, setToggleParkedSaleModal } = parkedSaleStore
  const [isVisiblePopOver, setVisiblePopOver] = useState(false)

  const alertLabel = (): JSX.Element | undefined => {
    const numberOfOrders = alertPaymentQueue.length
    let paidStatus
    let queueStatus
    switch (parkedSaleType) {
      case ParkedSaleTypes.PAID_ORDER_PLACED:
        paidStatus = 'Paid order'
        queueStatus = 'order placed'
        break
      case ParkedSaleTypes.PAID_IN_PROGRESS:
        paidStatus = 'Paid order'
        queueStatus = 'progress'
        break
      case ParkedSaleTypes.NOT_PAID_ORDER_PLACED:
        paidStatus = 'Unpaid order'
        queueStatus = 'order placed'
        break
      case ParkedSaleTypes.NOT_PAID_IN_PROGRESS:
        paidStatus = 'Unpaid order'
        queueStatus = 'progress'
        break
    }

    return (
      <>
        <strong>
          {numberOfOrders} {paidStatus}
          {numberOfOrders > 1 && `s`}
        </strong>{' '}
        in <strong>{queueStatus}</strong>
      </>
    )
  }

  const tableMessage = (): JSX.Element => {
    switch (parkedSaleType) {
      case ParkedSaleTypes.PAID_ORDER_PLACED:
        return (
          <>
            Set as <strong>Ready for pickup or out for delivery</strong> to let customers know.
          </>
        )
      case ParkedSaleTypes.PAID_IN_PROGRESS:
        return (
          <>
            Set as <strong>Completed</strong> when the order is delivered or picked up by the
            customer.
          </>
        )
      case ParkedSaleTypes.NOT_PAID_ORDER_PLACED:
        return (
          <>
            Set as <strong>Ready for pickup or out for delivery</strong> and/or complete the sale on
            the POS in-store
          </>
        )
      case ParkedSaleTypes.NOT_PAID_IN_PROGRESS:
        return <>Complete the sale on the POS in-store.</>
      default:
        return <></>
    }
  }

  const momentDate = (date: Date) => {
    if (compareOneHourFromNow(date)) {
      return (
        <>
          <p className='mb-0'>{moment(date).format('MMM D, h:mm a')}</p>({moment(date).fromNow()})
        </>
      )
    } else {
      return moment(date).format('MMM D, h:mm a')
    }
  }

  const columns = [
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      render: (paymentQueue: IPaymentQueueWithExternalSource) => (
        <Tag color={paymentQueue.externalSourceHexColor || 'green'}>
          {paymentQueue.externalSourceName || BLAZE_COMPANY}
        </Tag>
      ),
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (externalOrderId: number) => padStart(`${externalOrderId}` || '0', 4, '0'),
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      render: (date: Date) => momentDate(date),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (paymentStatus: PaymentQueueStatus) => (
        <ParkedSaleStatusButton
          status={paymentStatus}
          buttonStatus={ParkedSaleButtonStatus.ACTIVE}
        />
      ),
    },
  ]

  const dataSource = alertPaymentQueue.map((filteredPaymentQueue) => {
    return {
      key: filteredPaymentQueue.id,
      source: filteredPaymentQueue,
      id: filteredPaymentQueue.externalOrderId,
      created: filteredPaymentQueue.createDate,
      status: filteredPaymentQueue.status,
    }
  })

  const message: JSX.Element = (
    <div className='d-flex align-items-center justify-content-between'>
      <p style={{ marginBottom: 0, marginRight: 16 }}>{alertLabel() || ''}</p>
      <Popover
        trigger='click'
        placement='bottom'
        visible={isVisiblePopOver}
        onVisibleChange={() => {
          setVisiblePopOver(!isVisiblePopOver)
        }}
        content={
          <div className='ant-popover-inner-box'>
            <Alert message={tableMessage()} type='info' showIcon style={{ marginBottom: 16 }} />

            <Table
              bodyStyle={{ cursor: 'pointer' }}
              rowClassName={() => 'parked-sale-alert-box-row'}
              columns={columns}
              dataSource={dataSource}
              pagination={false}
              size='small'
              scroll={{ x: true, y: false }}
              onRow={(record) => {
                return {
                  onClick: () => {
                    setVisiblePopOver(false)
                    setToggleParkedSaleModal(true, record.key)
                  },
                }
              }}
            />
          </div>
        }
      >
        <p className='cursor-pointer underline-on-hover'>See more</p>
      </Popover>
    </div>
  )

  return (
    <Alert
      type='warning'
      showIcon
      className={`parked-sale-alert-type ${parkedSaleType}`}
      message={message}
    />
  )
}
